import type { AgentRequest, AlternateAgentInfo, HealthcareAgentInfo } from '@usacsmideo/types';

/**
 * ECN invite ↔ chart convergence (CU-86aj2q4fc).
 *
 * Pure logic shared by the chart-slot-driven My MIDEO Family hub and the careTeam
 * chart forms. Keeps the hub gate and the form's "Send Alerts" enablement in lock
 * step, and is the single place the three convergence questions are answered:
 *   1. Is this chart slot COMPLETE enough to invite?            → `isChartRecordComplete`
 *   2. Does the slot's stamped link still point at a VALID invite? → `isLinkValid`
 *   3. What should the hub show for this slot?                  → `resolveSlotStatus`
 *
 * Design decisions (locked 2026-06-16, redesign §3.2.1):
 *   - "Complete" = the FULL chart record incl. address. Friction accepted for data
 *     quality before any alert relationship is formed. HCA additionally requires
 *     its HCA-only `hasPowerOfAttorney`.
 *   - Link validity is by EMAIL MATCH, not the id alone: the stamped
 *     `agentRequestId` is trusted only while the slot's current `email` equals the
 *     linked request's `recipientEmail` (compared case-insensitively — the server
 *     stores `recipientEmail` lowercase-normalized). On divergence the link is
 *     treated as "not invited"; the id is left inert, never proactively cleared.
 */

/** Chart record for either agent kind — both share the same canonical shape. */
export type AgentChartRecord = AlternateAgentInfo | HealthcareAgentInfo;

/** A chart record is an HCA record iff it carries the HCA-only POA field. */
function isHcaRecord(record: AgentChartRecord): record is HealthcareAgentInfo {
  return 'hasPowerOfAttorney' in record;
}

function isNonEmpty(value: string | undefined): boolean {
  return typeof value === 'string' && value.trim().length > 0;
}

/** Case-insensitive, trimmed email equality (server stores recipientEmail lowercased). */
export function emailsMatch(a: string | undefined, b: string | undefined): boolean {
  if (!isNonEmpty(a) || !isNonEmpty(b)) return false;
  return a!.trim().toLowerCase() === b!.trim().toLowerCase();
}

/**
 * "Complete" = the full chart record: identity (`agent` name) + reachability
 * (`email`, `primaryPhone`) + full address (`street`, `city`, `state`, `ZIP`,
 * `country`) + both relationship descriptions. HCA adds `hasPowerOfAttorney`.
 *
 * An absent record is not complete.
 */
export function isChartRecordComplete(record: AgentChartRecord | undefined): boolean {
  if (!record) return false;
  const requiredCore: (keyof AgentChartRecord)[] = [
    'agent',
    'email',
    'primaryPhone',
    'street',
    'city',
    'state',
    'ZIP',
    'country',
    'agentToPatient',
    'patientToAgent',
  ];
  const coreComplete = requiredCore.every((k) => isNonEmpty(record[k] as string | undefined));
  if (!coreComplete) return false;
  if (isHcaRecord(record)) {
    return isNonEmpty(record.hasPowerOfAttorney);
  }
  return true;
}

/**
 * The slot's stamped link is valid iff it names a request AND that request's
 * `recipientEmail` still matches the slot's current `email`. Anything else
 * (no id, no matching request, diverged email) → not a valid link.
 */
export function isLinkValid(
  record: AgentChartRecord | undefined,
  request: AgentRequest | undefined,
): boolean {
  if (!record?.agentRequestId || !request) return false;
  if (request.id !== record.agentRequestId) return false;
  return emailsMatch(record.email, request.recipientEmail);
}

/**
 * The slot has an ORPHANED invite when it carries a stamped id that resolves to a
 * real request, but the slot's email has since diverged from that request's
 * `recipientEmail` — i.e. an active invite the patient has disconnected by editing
 * the email. Drives the patient-facing "re-invite" alert.
 */
export function isInviteOrphaned(
  record: AgentChartRecord | undefined,
  request: AgentRequest | undefined,
): boolean {
  if (!record?.agentRequestId || !request) return false;
  if (request.id !== record.agentRequestId) return false;
  // Orphaned only while the linked request is still live (waiting/accepted).
  if (request.status !== 'waiting' && request.status !== 'accepted') return false;
  return !emailsMatch(record.email, request.recipientEmail);
}

/**
 * Per-slot convergence state for the hub. Drives which button/label the slot row
 * renders.
 *
 *   - 'empty'      — no chart record (or no name): offer to add details.
 *   - 'incomplete' — record exists but fails the completeness gate: cross-link to
 *                    the chart form to finish it before inviting.
 *   - 'invitable'  — complete and not validly invited: offer "Invite to receive alerts".
 *   - 'orphaned'   — complete, but a live invite's email no longer matches: warn +
 *                    offer re-invite.
 *   - 'invited'    — complete with a valid live link: show invite status, no dup.
 */
export type SlotConvergenceState =
  | 'empty'
  | 'incomplete'
  | 'invitable'
  | 'orphaned'
  | 'invited';

export interface SlotStatus {
  state: SlotConvergenceState;
  /** The valid live request backing 'invited' (else undefined). */
  request?: AgentRequest;
}

/**
 * Resolve a slot's convergence state from its chart record and the candidate
 * request its `agentRequestId` points at. `request` is the request whose `id`
 * equals `record.agentRequestId` (the hub looks it up), or undefined if none.
 */
export function resolveSlotStatus(
  record: AgentChartRecord | undefined,
  request: AgentRequest | undefined,
): SlotStatus {
  if (!record || !isNonEmpty(record.agent)) return { state: 'empty' };
  if (!isChartRecordComplete(record)) return { state: 'incomplete' };
  if (isInviteOrphaned(record, request)) return { state: 'orphaned', request };
  if (isLinkValid(record, request)) {
    // A rejected/archived linked request is no longer a live invite → invitable.
    if (request!.status === 'waiting' || request!.status === 'accepted') {
      return { state: 'invited', request };
    }
  }
  return { state: 'invitable' };
}
